import { prefersReducedMotion } from "@theme/utilities";
import { ensureGsap } from "@theme/vp-motion";
import { getIntersectionRoot } from "@theme/scroll-container";
//#region assets/vp-stats-section.ts
/**
* Count-up animation for the stats section.
*
* Markup:
*   <vp-stats>
*     <span data-stat-counter data-value="1200" data-suffix="+" data-grouped="true">0</span>
*   </vp-stats>
*/
/**
* @param {number} value
* @param {{ grouped: boolean, suffix: string }} options
* @returns {string}
*/
function formatStat(value, { grouped, suffix }) {
	const rounded = Math.round(value);
	const text = grouped ? rounded.toLocaleString("nl-NL") : String(rounded);
	return suffix ? `${text}${suffix}` : text;
}
/**
* @param {Element} el
*/
function readCounterOptions(el) {
	return {
		value: Number.parseFloat(el.getAttribute("data-value") || "") || 0,
		grouped: el.getAttribute("data-grouped") === "true",
		suffix: el.getAttribute("data-suffix") || ""
	};
}
var VpStats = class extends HTMLElement {
	/** @type {IntersectionObserver | null} */
	#observer = null;
	#played = false;
	/** @type {typeof window.gsap | null} */
	#gsap = null;
	connectedCallback() {
		this.#init();
	}
	disconnectedCallback() {
		this.#observer?.disconnect();
		this.#observer = null;
	}
	async #init() {
		const counters = Array.from(this.querySelectorAll("[data-stat-counter]"));
		if (!counters.length) return;
		const reducedMotion = prefersReducedMotion();
		const designMode = document.documentElement.classList.contains("shopify-design-mode");
		counters.forEach((el) => {
			const options = readCounterOptions(el);
			el.textContent = formatStat(reducedMotion || designMode ? options.value : 0, options);
		});
		if (reducedMotion || designMode) return;
		this.#gsap = await ensureGsap();
		if (!this.#gsap) {
			counters.forEach((el) => {
				const options = readCounterOptions(el);
				el.textContent = formatStat(options.value, options);
			});
			return;
		}
		this.#observer = new IntersectionObserver((entries) => {
			if (!entries[entries.length - 1]?.isIntersecting || this.#played) return;
			this.#played = true;
			this.#animate(counters);
			this.#observer?.disconnect();
			this.#observer = null;
		}, {
			root: getIntersectionRoot(),
			rootMargin: "0px 0px -8% 0px",
			threshold: .2
		});
		this.#observer.observe(this);
	}
	/**
	* @param {Element[]} counters
	*/
	#animate(counters) {
		const gsap = this.#gsap;
		if (!gsap) return;
		counters.forEach((el, index) => {
			const options = readCounterOptions(el);
			const state = { val: 0 };
			gsap.to(state, {
				val: options.value,
				duration: 2,
				delay: index * .12,
				ease: "power2.out",
				onUpdate: () => {
					el.textContent = formatStat(state.val, options);
				}
			});
		});
	}
};
if (!customElements.get("vp-stats")) customElements.define("vp-stats", VpStats);
//#endregion
